import { ActivatedRoute } from '@angular/router';
import { Component, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { LocalSignupFormModel } from '@shared/models/register-models';

import { ModalService, ValidationService, ProxyService, AsyncValidatorWExt, AlertService, ConfirmModalService, ResolutionService, AccessibilityService, CiamFlagsService } from '@core/services';

import { AuthenticationService } from '@core/services';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { UpdateTrigger } from '@shared/components/validation-message';
import { Subscription } from 'rxjs';
import { Constants } from '@app/core/constants/core.constants';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.less'],
  providers: [AlertService]  
})
export class RegisterComponent implements OnInit, AfterViewInit, OnDestroy {

  public registerForm: UntypedFormGroup;
  public loading: boolean;
  public checkBoxLabel: Array<string> = new Array<string>('Email me offers and news');
  updateTrigger: any = UpdateTrigger;
  private subscriptions = new Subscription();
  constructor(
    public alertService: AlertService,
    private fBuilder: UntypedFormBuilder,
    private proxy: ProxyService,
    private registrationService: AuthenticationService,
    private modalService: ModalService,
    private confirmModalService: ConfirmModalService,
    private activatedRoute: ActivatedRoute,
    private resolution: ResolutionService,
    private accessibilityService: AccessibilityService,
    private ciamFlagsService: CiamFlagsService) {
    this.registrationService.setAlertService(this.alertService);
    this.loading = false;
    this.createRegisterForm();
    const formValue = JSON.parse(sessionStorage.getItem('signUpForm'));
    if (formValue) {
      this.registerForm.controls.firstname.setValue(formValue.firstname);
      this.registerForm.controls.lastname.setValue(formValue.lastname);
      this.registerForm.controls.email.setValue(formValue.email);
      this.registerForm.controls.addToEmail.setValue(formValue.addToEmail);
      this.registerForm.controls.phonenumber.setValue(formValue.phonenumber);
      this.registerForm.controls.zipcode.setValue(formValue.zipcode);
    }
  }
  ngOnInit(): void {
    this.subscriptions.add(
      this.resolution.width.subscribe(
        (width) => {
          if (width > environment.MAX_MOBILE_WIDTH) {
            this.accessibilityService.setAccessibilityWidgetCSSText(this.accessibilityService.DISPLAYINLINEBLOCKIMPORTANT);
          } else {
            this.accessibilityService.setAccessibilityWidgetCSSText(this.accessibilityService.SIGNINSIGNUPMODALPOSITION);
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.accessibilityService.setAccessibilityWidgetCSSText(this.accessibilityService.DISPLAYINLINEBLOCKIMPORTANT);
    this.subscriptions.unsubscribe();
    if (this.registrationService.resolvedUser.missingProperties.includes('Email') 
      || this.registrationService.resolvedUser.missingProperties.includes('FirstName')
      || this.registrationService.resolvedUser.missingProperties.includes('LastName')) {
      return;
    }
    this.registrationService.cleanUpAppleSigninQueryParams();
  }
  async ngAfterViewInit() {
    const snapshotParams = this.activatedRoute.snapshot.queryParams;
    if (snapshotParams[Constants.APPLE_SIGNIN_ORIGIN]) {
      await this.registrationService.handleAppleSignInRedirect(snapshotParams);
      this.loading = false;
    }
  }

  async appleAuth() {
    this.registrationService.appleAuth('register');
  }

  createRegisterForm() {
    this.registerForm = this.fBuilder.group({
      firstname: new UntypedFormControl(null, [Validators.required, ValidationService.firstNameLastNameValidator]),
      lastname: new UntypedFormControl(null, [Validators.required, ValidationService.firstNameLastNameValidator]),
      phonenumber: new UntypedFormControl(null, [
        ValidationService.phoneValidator
      ]),
      zipcode: new UntypedFormControl(null, [
        Validators.required,
        ValidationService.validateCardZip,
      ]),
      email: new UntypedFormControl(null, [Validators.required], [AsyncValidatorWExt.debounce(ValidationService.asyncEmailValidator, 800)]),
      passwords: this.fBuilder.group(
        {
          password: new UntypedFormControl(null, [
            Validators.required,
            ValidationService.passwordValidator
          ]),
          confPw: new UntypedFormControl(null, [Validators.required])
        },
        { validator: ValidationService.passwordMatch }
      ),
      addToEmail: new UntypedFormControl(null)
    });
  }

  onSubmit() {
    this.loading = true;
    const formModel = <LocalSignupFormModel>this.registerForm.value;
    sessionStorage.setItem('signUpForm', JSON.stringify(this.registerForm.value));
    this.validateCustomerInfo(formModel);
  }

  validateCustomerInfo(formModel) {
    const parameters = [{
      'Name': 'firstname',
      'Value': this.registerForm.value.firstname
    }, {
      'Name': 'lastname',
      'Value': this.registerForm.value.lastname
    }, {
      'Name': 'zipcode',
      'Value': this.registerForm.value.zipcode
    }, {
      'Name': 'email',
      'Value': this.registerForm.value.email
    }];
    this.proxy.httpPost(ApiRoutes.VALIDATE_CUSTOMERS, parameters).subscribe(
      (success) => {
        this.registrationService.setInternalRequest(formModel, true);
        this.loading = false;
      },
      (err) => {
        this.loading = false;
        if (err.error && err.error ) {
          if (err.error instanceof Array ) {
            const messg = [];
            err.error.forEach(item => {
              messg.push('<p>' + item.message + '</p>');
            });
            const logmsg = messg.join(' ');
            this.confirmModalService.show(
              '', 'Error!',
              logmsg,
              '', '', 'OK, Got It', true, ''
            );
          } else {
            this.alertService.error(err.error.message);
          }
        }
      }
    );
  }

  facebookAuth() {
    this.loading = true;
    this.alertService.clear();
    this.registrationService.facebookRegister(true);
    this.loading = false;
  }

  googleAuth() {
    this.loading = true;
    this.alertService.clear();
    this.registrationService.googleRegister(true);
    this.loading = false;
  }

  get auth0PasswordlessSignup(): boolean {
    return this.ciamFlagsService.isAuth0PasswordlessSignup();
  }

  showModal(name: string, autoHide = true) {
    this.modalService.setModalSelector(name);
    this.modalService.showModal(autoHide);
  }

  showSignInModal() {
    this.showModal(this.modalService.getSignInModalSelector());
  }

}
